/**
 * Copy for the legal pages (/policy and /tos). Each section body is a list of
 * blocks: a string is a paragraph, an array is a bullet list.
 */
import { SITE } from '@/data/site';

const ENTITY = 'NioDelta Web Studio';
const UPDATED = '16 October 2026';

export const LEGAL = {
  policy: {
    path: '/policy',
    eyebrow: 'Privacy Policy — NioDelta',
    headline: 'What we collect, and what we do with it.',
    updated: UPDATED,
    intro: `This policy explains how ${ENTITY} (“NioDelta”, “we”, “us”) handles personal data when you visit niodelta.com or contact us about a project. It is written to be read, not to be skimmed past.`,
    sections: [
      {
        title: 'Who we are',
        body: [
          `${ENTITY} is a software and web development studio based in Pune, Maharashtra, India. We decide how the personal data described here is used. You can reach us at ${SITE.email}.`,
        ],
      },
      {
        title: 'Data we collect',
        body: [
          'We collect only what we need to reply to you and to understand how the site is used.',
          [
            'Details you send us: your name, email address, phone number, company, budget range and anything you write in your message, when you contact us by WhatsApp, email or phone.',
            'Usage data: pages visited, referring page, approximate location derived from IP address, device and browser type, and the time of your visit.',
            'Advertising data: if you arrive from a Google ad, Google Ads sets cookies or similar identifiers so we can measure whether the ad led to an enquiry.',
            'Server logs: our hosting provider records IP address, requested URL and user agent for security and troubleshooting.',
          ],
          'The contact form on this site does not store what you type on our servers. It opens WhatsApp with your message pre-filled, and you choose whether to send it.',
        ],
      },
      {
        title: 'Google account data',
        body: [
          'NioDelta operates an internal tool that connects to Google Ads through Google sign-in (OAuth). It asks for one permission, the Google Ads API scope (https://www.googleapis.com/auth/adwords), and only after the account owner approves it on Google’s consent screen.',
          'With that permission the tool can access, for the Google Ads accounts the signed-in user manages:',
          [
            'Account details: account name, customer ID, currency and time zone.',
            'Campaign data: campaigns, ad groups, ads, keywords, budgets, bids and targeting settings.',
            'Performance data: impressions, clicks, cost, conversions and search terms.',
            'Keyword planning data: keyword ideas, search volumes and bid estimates.',
          ],
          'We use this data only to research keywords, report on performance, and create or change campaigns as the account owner instructs. The tool does not read Gmail, Drive, Calendar, contacts or any other Google service, and it does not see your Google password.',
          'Google issues an access token and a refresh token to the tool. These are stored on our server with access limited to the people who run the tool. Reports drawn from the data are kept for as long as the engagement lasts and are deleted on request.',
          'We do not sell Google user data, we do not use it for advertising profiles, credit decisions or any purpose unrelated to managing the account, and we do not use it to develop or train generalised artificial intelligence or machine learning models. We do not transfer it to anyone except service providers that process it on our behalf, or where the law requires it. People at NioDelta read the data only to deliver the service, to investigate a security issue, or to comply with the law.',
          'Our use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.',
          `You can withdraw access at any time at myaccount.google.com/permissions. To have stored tokens and reports deleted, email ${SITE.email}; we delete them within 30 days.`,
        ],
      },
      {
        title: 'Instagram comments and messages',
        body: [
          'When you comment on a post by @niodelta.studio or send it a direct message on Instagram, we receive your comment or message, your Instagram username and your Instagram user ID through Meta’s official Instagram API. We use them only to reply to you, for example a public reply under your comment or a direct message with the details you asked for.',
          'Our reply tool runs on our own server. It does not read your password, your other conversations, your followers or any content beyond what you send to @niodelta.studio. Access tokens for our Instagram account are stored encrypted.',
          `We keep comments, messages and reply logs only as long as we need them to answer you and follow up on your enquiry, and no longer than 12 months. To have them deleted, email ${SITE.email}; we delete them within 30 days. Instructions are also at https://reply.niodelta.com/data-deletion.`,
        ],
      },
      {
        title: 'How we use it',
        body: [
          [
            'To reply to your enquiry and prepare a quote or proposal.',
            'To deliver and support work you have engaged us for.',
            'To measure which pages and ads bring useful enquiries, so we can improve the site.',
            'To keep the site secure and to meet our legal and tax obligations.',
          ],
          'We do not sell personal data, and we do not use it for automated decisions about you.',
        ],
      },
      {
        title: 'Cookies and tracking',
        body: [
          'This site uses the Google Ads tag for conversion measurement. It may set cookies and read identifiers on your device. The site also loads fonts from Google Fonts and images from Unsplash, which means those providers receive your IP address when the page loads.',
          'You can block or delete cookies in your browser settings, and you can control ad personalisation at adssettings.google.com. The site works without cookies.',
        ],
      },
      {
        title: 'Who we share it with',
        body: [
          'We share personal data only with providers that help us run the studio, and only as far as they need it:',
          [
            'Google (advertising measurement, fonts, email).',
            'Meta (WhatsApp), when you choose to message us there.',
            'Our hosting and infrastructure providers.',
            'Professional advisers, or authorities, where the law requires it.',
          ],
          'Some of these providers process data outside India. Where they do, the transfer is governed by their terms and by applicable Indian law.',
        ],
      },
      {
        title: 'How long we keep it',
        body: [
          'Enquiries that do not become projects are deleted within 24 months of our last contact. Project records, contracts and invoices are kept for as long as tax and company law require, usually eight years. Server logs are kept for a short period and then rotated out.',
        ],
      },
      {
        title: 'Your rights',
        body: [
          'Under the Digital Personal Data Protection Act, 2023 and other applicable law, you can ask us to:',
          [
            'Tell you what personal data we hold about you.',
            'Correct or update it.',
            'Delete it, where we have no legal reason to keep it.',
            'Stop using it, by withdrawing consent you gave earlier.',
            'Nominate another person to exercise these rights on your behalf.',
          ],
          `Email ${SITE.email} with your request. We reply within 30 days and do not charge for it.`,
        ],
      },
      {
        title: 'Security',
        body: [
          'The site is served over HTTPS. Access to enquiries and project files is limited to the people working on them. No system is perfectly secure; if a breach affects your data we will tell you and the relevant authority as the law requires.',
        ],
      },
      {
        title: 'Children',
        body: [
          'This site is meant for businesses. We do not knowingly collect personal data from anyone under 18. If you believe a child has sent us their data, email us and we will delete it.',
        ],
      },
      {
        title: 'Grievances and contact',
        body: [
          `Questions or complaints about how we handle personal data go to our grievance contact at ${SITE.email}, or by phone on ${SITE.phone}. If you are not satisfied with our reply, you may complain to the Data Protection Board of India.`,
        ],
      },
      {
        title: 'Changes to this policy',
        body: [
          'We update this page when our practices change. The date at the top shows the latest version. Material changes are flagged on this page for at least 30 days.',
        ],
      },
    ],
  },

  tos: {
    path: '/tos',
    eyebrow: 'Terms of Service — NioDelta',
    headline: 'The terms for using this site.',
    updated: UPDATED,
    intro: `These terms apply to niodelta.com, operated by ${ENTITY} (“NioDelta”, “we”, “us”). By using the site you accept them. If you do not accept them, please do not use the site.`,
    sections: [
      {
        title: 'What this site is',
        body: [
          'This site describes our studio, our services and our past work. It is information, not an offer. Client work is governed by a separate written agreement signed by both sides, and that agreement takes priority over these terms.',
        ],
      },
      {
        title: 'Prices, timelines and quotes',
        body: [
          'Prices, package details and timelines shown on the site are indicative and can change without notice. A price becomes binding only when it appears in a written quote or agreement that we have both accepted.',
        ],
      },
      {
        title: 'Acceptable use',
        body: [
          'When you use the site, you agree not to:',
          [
            'Break any law, or infringe anyone else’s rights.',
            'Attempt to gain unauthorised access to the site, its servers or connected systems.',
            'Disrupt the site, or probe, scan or test it for vulnerabilities without our written permission.',
            'Scrape or copy the site in bulk, or use automated tools that place an unreasonable load on it.',
            'Send us false information, spam or malicious code.',
          ],
        ],
      },
      {
        title: 'Intellectual property',
        body: [
          'The text, design, code, graphics and logos on this site belong to NioDelta or its licensors. You may view the site and share links to it. You may not copy, republish or reuse its content commercially without our written permission.',
          'Client names, logos and case-study material remain the property of their owners and are shown with permission or for identification.',
        ],
      },
      {
        title: 'What you send us',
        body: [
          'When you send us a brief, a message or files, you confirm that you have the right to share them. We use them only to assess and reply to your enquiry. We treat project information as confidential, though an unsolicited idea sent to us does not create a confidentiality obligation unless we agree to one in writing.',
        ],
      },
      {
        title: 'Third-party links and services',
        body: [
          'The site links to services we do not control, including WhatsApp, Instagram, LinkedIn, X and Dribbble. Their own terms and privacy policies apply. We are not responsible for their content or conduct.',
        ],
      },
      {
        title: 'No warranty',
        body: [
          'The site is provided “as is”. We work to keep it accurate and available, but we do not promise that it is error-free, uninterrupted or suited to a particular purpose. Case-study results describe past projects and are not a guarantee of future outcomes.',
        ],
      },
      {
        title: 'Limitation of liability',
        body: [
          'To the extent the law allows, NioDelta is not liable for indirect, incidental or consequential loss, or for loss of profit, data or business, that arises from using or being unable to use this site. Nothing in these terms limits liability that cannot be limited under Indian law.',
        ],
      },
      {
        title: 'Indemnity',
        body: [
          'If your misuse of the site or your breach of these terms leads to a claim against NioDelta, you agree to cover the reasonable costs and losses we incur as a result.',
        ],
      },
      {
        title: 'Privacy',
        body: ['Our Privacy Policy at niodelta.com/policy explains how we handle personal data. It forms part of these terms.'],
      },
      {
        title: 'Governing law and disputes',
        body: [
          'These terms are governed by the laws of India. The courts at Pune, Maharashtra have exclusive jurisdiction over any dispute relating to the site or these terms.',
        ],
      },
      {
        title: 'Changes and contact',
        body: [
          `We may update these terms. The date at the top shows the latest version, and continued use of the site means you accept it. Questions go to ${SITE.email}.`,
        ],
      },
    ],
  },
};
